import React, { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import axios from "axios";
import QRCode from "qrcode";
import { API_URL } from "../config";
import { SectionHeading } from "../utils";

const RECORD_NAME_MAX_LENGTH = 25;
const SOCIAL_SERVICE_OPTIONS = [
    { label: "Airbnb", value: "airbnb", base: "https://www.airbnb.com/users/show/" },
    { label: "Discord", value: "discord", base: "https://discord.gg/" },
    { label: "DoorDash", value: "doordash", base: "https://www.doordash.com/store/" },
    { label: "Facebook", value: "facebook", base: "https://www.facebook.com/" },
    { label: "Instagram", value: "instagram", base: "https://www.instagram.com/" },
    { label: "LinkedIn", value: "linkedin", base: "https://www.linkedin.com/in/" },
    { label: "Pinterest", value: "pinterest", base: "https://www.pinterest.com/" },
    { label: "Snapchat", value: "snapchat", base: "https://www.snapchat.com/add/" },
    { label: "Telegram", value: "telegram", base: "https://t.me/" },
    { label: "TikTok", value: "tiktok", base: "https://www.tiktok.com/@" },
    { label: "X (Twitter)", value: "x", base: "https://x.com/" },
    { label: "Yelp", value: "yelp", base: "https://www.yelp.com/biz/" },
    { label: "YouTube", value: "youtube", base: "https://www.youtube.com/@" },
];

const TYPE_CONFIG = {
    text: {
        label: "Plain Text",
        mode: "download",
        downloadExtension: "txt",
        downloadMime: "text/plain;charset=utf-8",
        description: "Encode any text and save it as a downloadable text file record.",
        fields: [{ key: "text", label: "Text to encode", required: true, multiline: true, rows: 4, md: 12 }],
        buildPayload: (v) => v.text,
    },
    email: {
        label: "Email",
        mode: "url",
        description: "Opens the default mail app. Saved like URL records so it can be edited later.",
        fields: [
            { key: "email", label: "Email address", required: true, md: 6 },
            { key: "subject", label: "Subject", md: 6 },
            { key: "body", label: "Message body", multiline: true, rows: 3, md: 12 },
        ],
        buildPayload: (v) => {
            const query = new URLSearchParams();
            if (v.subject) query.set("subject", v.subject);
            if (v.body) query.set("body", v.body);
            const suffix = query.toString();
            return `mailto:${v.email}${suffix ? `?${suffix}` : ""}`;
        },
    },
    sms: {
        label: "SMS",
        mode: "url",
        description: "Launches a text message draft. Saved as URL-style record.",
        fields: [
            { key: "smsNumber", label: "Phone number", required: true, md: 6 },
            { key: "smsMessage", label: "Message", multiline: true, rows: 3, md: 6 },
        ],
        buildPayload: (v) => {
            const query = new URLSearchParams();
            if (v.smsMessage) query.set("body", v.smsMessage);
            return `sms:${v.smsNumber || ""}${query.toString() ? `?${query.toString()}` : ""}`;
        },
    },
    phone: {
        label: "Phone",
        mode: "url",
        description: "Starts a phone call. Saved as URL-style record.",
        fields: [{ key: "phone", label: "Phone number", required: true, md: 12 }],
        buildPayload: (v) => `tel:${v.phone}`,
    },
    wifi: {
        label: "Wi-Fi",
        mode: "download",
        downloadExtension: "txt",
        downloadMime: "text/plain;charset=utf-8",
        description: "Creates a downloadable Wi-Fi payload file for scan-and-connect.",
        fields: [
            { key: "ssid", label: "Network name (SSID)", required: true, md: 6 },
            { key: "password", label: "Password", md: 6 },
            { key: "security", label: "Security", select: true, options: ["WPA", "WEP", "nopass"], required: true, md: 6 },
            { key: "hidden", label: "Hidden network", select: true, options: ["false", "true"], required: true, md: 6 },
        ],
        buildPayload: (v) => `WIFI:T:${v.security};S:${v.ssid};P:${v.password || ""};H:${v.hidden ? "true" : "false"};;`,
    },
    vcard: {
        label: "Contact (vCard)",
        mode: "download",
        downloadExtension: "vcf",
        downloadMime: "text/vcard;charset=utf-8",
        description: "Saves a downloadable vCard file for scanners to import contacts.",
        fields: [
            { key: "fullName", label: "Full name", required: true, md: 6 },
            { key: "organization", label: "Organization", md: 6 },
            { key: "title", label: "Job title", md: 6 },
            { key: "contactPhone", label: "Phone", md: 6 },
            { key: "contactEmail", label: "Email", md: 6 },
            { key: "contactUrl", label: "Website", md: 6 },
            { key: "address", label: "Address", md: 12 },
        ],
        buildPayload: (v) => [
            "BEGIN:VCARD",
            "VERSION:3.0",
            `FN:${v.fullName}`,
            v.organization ? `ORG:${v.organization}` : "",
            v.title ? `TITLE:${v.title}` : "",
            v.contactPhone ? `TEL:${v.contactPhone}` : "",
            v.contactEmail ? `EMAIL:${v.contactEmail}` : "",
            v.contactUrl ? `URL:${v.contactUrl}` : "",
            v.address ? `ADR:${v.address}` : "",
            "END:VCARD",
        ].filter(Boolean).join("\n"),
    },
    geo: {
        label: "Location (Geo)",
        mode: "url",
        description: "Opens map coordinates. Saved as URL-style record.",
        fields: [
            { key: "latitude", label: "Latitude", required: true, md: 6 },
            { key: "longitude", label: "Longitude", required: true, md: 6 },
        ],
        buildPayload: (v) => `geo:${v.latitude},${v.longitude}`,
    },
    event: {
        label: "Calendar Event",
        mode: "download",
        downloadExtension: "ics",
        downloadMime: "text/calendar;charset=utf-8",
        description: "Creates a downloadable calendar file that users can save on scan.",
        fields: [
            { key: "eventTitle", label: "Event title", required: true, md: 6 },
            { key: "eventLocation", label: "Location", md: 6 },
            { key: "eventStart", label: "Start date & time", type: "datetime-local", required: true, md: 6 },
            { key: "eventEnd", label: "End date & time", type: "datetime-local", md: 6 },
            { key: "eventNotes", label: "Notes", multiline: true, rows: 3, md: 12 },
        ],
        buildPayload: (v) => {
            const formatDate = (value) => value?.replace(/[-:]/g, "").replace("T", "") || "";
            return [
                "BEGIN:VCALENDAR",
                "VERSION:2.0",
                "BEGIN:VEVENT",
                `SUMMARY:${v.eventTitle}`,
                v.eventLocation ? `LOCATION:${v.eventLocation}` : "",
                v.eventStart ? `DTSTART:${formatDate(v.eventStart)}` : "",
                v.eventEnd ? `DTEND:${formatDate(v.eventEnd)}` : "",
                v.eventNotes ? `DESCRIPTION:${v.eventNotes}` : "",
                "END:VEVENT",
                "END:VCALENDAR",
            ].filter(Boolean).join("\n");
        },
    },
    whatsapp: {
        label: "WhatsApp",
        mode: "url",
        description: "Starts a WhatsApp chat. Saved as URL-style record.",
        fields: [
            { key: "waNumber", label: "WhatsApp number", required: true, md: 6 },
            { key: "waMessage", label: "Message", multiline: true, rows: 3, md: 6 },
        ],
        buildPayload: (v) => `https://wa.me/${(v.waNumber || "").replace(/\D/g, "")}?text=${encodeURIComponent(v.waMessage || "")}`,
    },
    bitcoin: {
        label: "Bitcoin Payment",
        mode: "url",
        description: "Creates a payment URI. Saved as URL-style record.",
        fields: [
            { key: "wallet", label: "Wallet address", required: true, md: 12 },
            { key: "amount", label: "Amount (BTC)", md: 4 },
            { key: "label", label: "Label", md: 4 },
            { key: "message", label: "Message", md: 4 },
        ],
        buildPayload: (v) => {
            const query = new URLSearchParams();
            if (v.amount) query.set("amount", v.amount);
            if (v.label) query.set("label", v.label);
            if (v.message) query.set("message", v.message);
            return `bitcoin:${(v.wallet || "").trim()}${query.toString() ? `?${query.toString()}` : ""}`;
        },
    },
    facetime: {
        label: "FaceTime",
        mode: "url",
        description: "Starts a FaceTime call link. Saved as URL-style record.",
        fields: [{ key: "faceTimeHandle", label: "Phone or email", required: true, md: 12 }],
        buildPayload: (v) => `facetime:${v.faceTimeHandle}`,
    },
    service: {
        label: "Social / Common Service",
        mode: "url",
        description: "Quickly create links to common social profiles and services.",
        fields: [
            { key: "service", label: "Service", select: true, options: SOCIAL_SERVICE_OPTIONS.map((item) => item.value), required: true, md: 6 },
            { key: "serviceHandle", label: "Handle / ID / slug", required: true, md: 6 },
            { key: "serviceCustomUrl", label: "Or paste full URL (optional override)", md: 12 },
        ],
        buildPayload: (v) => {
            if (v.serviceCustomUrl) return v.serviceCustomUrl.trim();
            const selected = SOCIAL_SERVICE_OPTIONS.find((item) => item.value === v.service);
            return `${selected?.base || "https://"}${(v.serviceHandle || "").replace(/^\/+/, "")}`;
        },
    },
};

const TYPE_OPTIONS = Object.entries(TYPE_CONFIG)
    .map(([value, config]) => ({ value, ...config }))
    .sort((a, b) => a.label.localeCompare(b.label));
const INITIAL_VALUES = { security: "WPA", hidden: false, service: "airbnb" };

export default function UploadFlexibleQr() {
    const [name, setName] = useState("");
    const [type, setType] = useState("text");
    const [values, setValues] = useState(INITIAL_VALUES);
    const [payload, setPayload] = useState("");
    const [qrDataUrl, setQrDataUrl] = useState("");
    const [generateLoading, setGenerateLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    const activeType = TYPE_CONFIG[type];
    const activeFields = useMemo(() => activeType.fields, [activeType]);

    const handleValueChange = (key, value) => {
        setValues((prev) => ({ ...prev, [key]: key === "hidden" ? value === "true" : value }));
    };

    const handleTypeChange = (event) => {
        setType(event.target.value);
        setValues(INITIAL_VALUES);
        setPayload("");
        setQrDataUrl("");
    };

    const validate = () => {
        const trimmedName = name.trim();
        if (!trimmedName) return "Record name is required.";
        if (trimmedName.length > RECORD_NAME_MAX_LENGTH) return `Record name must be ${RECORD_NAME_MAX_LENGTH} characters or fewer.`;

        const missing = activeFields.find((field) => field.required && !String(values[field.key] ?? "").trim());
        if (missing) return `${missing.label} is required.`;

        return "";
    };

    const generateQr = async (event) => {
        event.preventDefault();
        const validationError = validate();
        if (validationError) {
            setToast({ open: true, message: validationError, severity: "error" });
            return;
        }

        setGenerateLoading(true);
        try {
            const generatedPayload = activeType.buildPayload(values);
            const generatedQr = await QRCode.toDataURL(generatedPayload, { width: 340, margin: 1 });
            setPayload(generatedPayload);
            setQrDataUrl(generatedQr);
            setToast({ open: true, message: "QR code generated successfully.", severity: "success" });
        } catch {
            setToast({ open: true, message: "Unable to generate QR code.", severity: "error" });
        } finally {
            setGenerateLoading(false);
        }
    };

    const saveUrlRecord = async (trimmedName, generatedPayload) => {
        const formData = new FormData();
        formData.append("name", trimmedName);
        formData.append("url", generatedPayload);

        const response = await axios.post(API_URL + "upload_url_qr.php", formData, { withCredentials: true });
        if (!response.data?.success) {
            throw new Error(response.data?.message || "Failed to save URL record.");
        }
        return response.data?.message || "Record saved successfully.";
    };

    const saveDownloadRecord = async (trimmedName, generatedPayload) => {
        const blob = new Blob([generatedPayload], { type: activeType.downloadMime || "text/plain;charset=utf-8" });
        const fileName = `${trimmedName.replace(/\s+/g, "-").toLowerCase()}.${activeType.downloadExtension || "txt"}`;
        const file = new File([blob], fileName, { type: blob.type });

        const formData = new FormData();
        formData.append("restaurant_name", trimmedName);
        formData.append("menu_file", file);

        const response = await axios.post(API_URL + "upload-doc.php", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
        });

        if (!response.data?.success) {
            throw new Error(response.data?.message || "Failed to save downloadable record.");
        }
        return response.data?.message || "Downloadable record saved successfully.";
    };

    const handleSaveRecord = async () => {
        const validationError = validate();
        if (validationError) {
            setToast({ open: true, message: validationError, severity: "error" });
            return;
        }

        const generatedPayload = payload || activeType.buildPayload(values);
        const trimmedName = name.trim();

        setSaveLoading(true);
        try {
            const message = activeType.mode === "url"
                ? await saveUrlRecord(trimmedName, generatedPayload)
                : await saveDownloadRecord(trimmedName, generatedPayload);

            setToast({ open: true, message, severity: "success" });
        } catch (error) {
            setToast({ open: true, message: error.message || "Failed to save record.", severity: "error" });
        } finally {
            setSaveLoading(false);
        }
    };

    const copyPayload = async () => {
        if (!payload) return;
        try {
            await navigator.clipboard.writeText(payload);
            setToast({ open: true, message: "Payload copied to clipboard.", severity: "success" });
        } catch {
            setToast({ open: true, message: "Clipboard access unavailable.", severity: "error" });
        }
    };

    return (
        <Container maxWidth="md" className="form-shell upload-url-shell">
            <Box className="form-shell-badge">
                <AppsRoundedIcon fontSize="small" />
                <Typography variant="caption">Flexible QR encoder</Typography>
            </Box>

            <SectionHeading sx={{ marginTop: "-8px", textAlign: "center" }}>Create editable dynamic QR records</SectionHeading>
            <Typography variant="h6" gutterBottom className="form-subheading" sx={{ textAlign: "center", fontSize: { xs: "1.05rem", md: "1.2rem" } }}>
                Link-based types are saved like URL records, while payload-based types are saved as downloadable files for end users.
            </Typography>

            <Paper
                variant="outlined"
                sx={{
                    p: { xs: 2.25, sm: 2.75 },
                    borderRadius: 3,
                    mb: 2,
                    borderColor: "rgba(99,102,241,0.2)",
                    background: "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,250,255,0.9))",
                    boxShadow: "0 10px 22px rgba(37, 99, 235, 0.08)",
                }}
            >
                <Stack spacing={1.5}>
                    <TextField
                        label="Record name"
                        helperText={`${name.length}/${RECORD_NAME_MAX_LENGTH} characters`}
                        value={name}
                        onChange={(event) => setName(event.target.value.slice(0, RECORD_NAME_MAX_LENGTH))}
                        required
                        fullWidth
                        className="form-input"
                    />

                    <FormControl fullWidth>
                        <InputLabel id="qr-type-label">QR Type</InputLabel>
                        <Select labelId="qr-type-label" label="QR Type" value={type} onChange={handleTypeChange} className="form-input">
                            {TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                        <Chip size="small" color={activeType.mode === "url" ? "info" : "success"} variant="outlined" label={activeType.mode === "url" ? "URL-style editable record" : "Downloadable file record"} />
                        <Typography variant="body2" color="text.secondary">{activeType.description}</Typography>
                    </Stack>
                </Stack>
            </Paper>

            <form onSubmit={generateQr} className="form-stack">
                <Grid container spacing={1.5}>
                    {activeFields.map((field) => (
                        <Grid key={field.key} size={{ xs: 12, md: field.md || 12 }}>
                            {field.select ? (
                                <FormControl fullWidth>
                                    <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
                                    <Select
                                        labelId={`${field.key}-label`}
                                        label={field.label}
                                        value={String(values[field.key] ?? field.options[0])}
                                        onChange={(event) => handleValueChange(field.key, event.target.value)}
                                        className="form-input"
                                    >
                                        {field.options.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {field.key === "service"
                                                    ? SOCIAL_SERVICE_OPTIONS.find((serviceOption) => serviceOption.value === option)?.label || option
                                                    : option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <TextField
                                    label={field.label}
                                    value={values[field.key] ?? ""}
                                    onChange={(event) => handleValueChange(field.key, event.target.value)}
                                    required={field.required}
                                    multiline={field.multiline}
                                    rows={field.rows}
                                    type={field.type || "text"}
                                    fullWidth
                                    className="form-input"
                                    InputLabelProps={field.type === "datetime-local" ? { shrink: true } : undefined}
                                />
                            )}
                        </Grid>
                    ))}
                </Grid>

                <Button type="submit" variant="contained" fullWidth className="form-submit-btn" startIcon={<QrCode2RoundedIcon />} disabled={generateLoading}>
                    {generateLoading ? <CircularProgress size={24} color="inherit" /> : "Generate QR Code"}
                </Button>
            </form>

            {qrDataUrl && (
                <Paper
                    variant="outlined"
                    className="qr-preview-block"
                    sx={{
                        p: { xs: 2.25, sm: 2.75 },
                        borderRadius: 3,
                        borderColor: "rgba(99,102,241,0.22)",
                        background: "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(242,247,255,0.9))",
                    }}
                >
                    <Box className="qr-preview-header">
                        <Typography variant="h6">Generated QR Code</Typography>
                        <Typography variant="body2">Download locally, copy payload, or save this as a record now.</Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                        <Box component="img" src={qrDataUrl} alt="Generated flexible QR" className="qr-preview-image" sx={{ maxWidth: 320, width: "100%" }} />
                    </Box>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <Button variant="outlined" className="qr-save-btn" startIcon={<DownloadRoundedIcon />} component="a" href={qrDataUrl} download={`${type}-qr.png`}>
                            Download PNG
                        </Button>
                        <Button variant="outlined" className="qr-save-btn" startIcon={<ContentCopyRoundedIcon />} onClick={copyPayload}>
                            Copy Payload
                        </Button>
                        <Button
                            variant="outlined"
                            className="qr-save-btn"
                            startIcon={activeType.mode === "url" ? <LinkRoundedIcon /> : <CloudUploadRoundedIcon />}
                            onClick={handleSaveRecord}
                            disabled={saveLoading}
                        >
                            {saveLoading ? <CircularProgress size={24} color="inherit" /> : `Save ${activeType.mode === "url" ? "URL" : "File"} Record`}
                        </Button>
                    </Stack>

                    <TextField label="Encoded payload" value={payload} multiline rows={4} fullWidth className="form-input" InputProps={{ readOnly: true }} />
                </Paper>
            )}

            <Snackbar open={toast.open} autoHideDuration={3200} onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
                <Alert severity={toast.severity} onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
